import React, { useState, useEffect } from 'react';
import api from '../utils/api';

function Teams() {
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showMembersModal, setShowMembersModal] = useState(null); // team object
  const [createForm, setCreateForm] = useState({ name: '', description: '' });
  const [createError, setCreateError] = useState('');
  const [createLoading, setCreateLoading] = useState(false);
  const [allUsers, setAllUsers] = useState([]);
  const [addUserForm, setAddUserForm] = useState('');
  const [addUserError, setAddUserError] = useState('');
  const [addUserLoading, setAddUserLoading] = useState(false);

  useEffect(() => {
    fetchTeams();
  }, []);

  const fetchTeams = async () => {
    setLoading(true);
    try {
      const res = await api.get('/teams');
      setTeams(res.data.data || []);
    } catch (err) {
      console.error('Failed to load teams:', err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const res = await api.get('/auth/users').catch(() => ({ data: { data: [] } }));
      setAllUsers(res.data.data || res.data.users || []);
    } catch (err) {
      console.error('Failed to load users:', err.message);
    }
  };

  const createTeam = async () => {
    if (!createForm.name.trim()) {
      setCreateError('Team name is required');
      return;
    }
    setCreateLoading(true);
    setCreateError('');
    try {
      await api.post('/teams', createForm);
      setShowCreateModal(false);
      setCreateForm({ name: '', description: '' });
      fetchTeams();
    } catch (err) {
      setCreateError(err.response?.data?.error || 'Failed to create team');
    } finally {
      setCreateLoading(false);
    }
  };

  const deleteTeam = async (id) => {
    if (!window.confirm('Archive this team? Existing project memberships will remain.')) return;
    try {
      await api.delete(`/teams/${id}`);
      fetchTeams();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to archive team');
    }
  };

  const openMembersModal = (team) => {
    setShowMembersModal(team);
    setAddUserForm('');
    setAddUserError('');
    fetchUsers();
  };

  const addUserToTeam = async () => {
    if (!addUserForm) { setAddUserError('Select a user'); return; }
    setAddUserLoading(true);
    setAddUserError('');
    try {
      await api.post(`/teams/${showMembersModal._id}/members`, { userId: addUserForm });
      const res = await api.get(`/teams/${showMembersModal._id}`);
      setShowMembersModal(res.data.data);
      setAddUserForm('');
      fetchTeams();
    } catch (err) {
      setAddUserError(err.response?.data?.error || 'Failed to add user');
    } finally {
      setAddUserLoading(false);
    }
  };

  const removeUserFromTeam = async (userId) => {
    try {
      await api.delete(`/teams/${showMembersModal._id}/members/${userId}`);
      const res = await api.get(`/teams/${showMembersModal._id}`);
      setShowMembersModal(res.data.data);
      fetchTeams();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to remove member');
    }
  };

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Teams</h1>
          <p className="page-subtitle">Organize users into teams and assign them to projects</p>
        </div>
        <button className="btn btn-primary" onClick={() => { setCreateForm({ name: '', description: '' }); setCreateError(''); setShowCreateModal(true); }}>
          + New Team
        </button>
      </div>

      <div className="container-fluid">
        {loading ? (
          <div className="zq-spinner-wrap">
            <div className="spinner-border" role="status"><span className="visually-hidden">Loading…</span></div>
          </div>
        ) : teams.length === 0 ? (
          <div className="card text-center py-5">
            <div className="card-body">
              <div style={{ fontSize: 48 }}>👥</div>
              <h5 className="mt-3">No teams yet</h5>
              <p className="text-muted">Create a team to group users and grant them project access together.</p>
              <button className="btn btn-primary" onClick={() => setShowCreateModal(true)}>Create First Team</button>
            </div>
          </div>
        ) : (
          <div className="row">
            {teams.map(team => (
              <div key={team._id || team.id} className="col-md-4 mb-4">
                <div className="card h-100">
                  <div className="card-body">
                    <div className="d-flex justify-content-between align-items-start">
                      <div>
                        <h5 className="card-title mb-1">👥 {team.name}</h5>
                        {team.description && <p className="text-muted small mb-2">{team.description}</p>}
                      </div>
                      <button className="btn btn-outline-danger btn-sm" onClick={() => deleteTeam(team._id || team.id)}>Archive</button>
                    </div>
                    <div className="mt-2">
                      <span className="badge bg-secondary bg-opacity-15 text-dark me-2">
                        {(team.members || []).length} member{(team.members || []).length !== 1 ? 's' : ''}
                      </span>
                    </div>
                    {(team.members || []).length > 0 && (
                      <div className="mt-2">
                        {team.members.slice(0, 3).map((m, i) => (
                          <span key={i} className="badge bg-light text-dark border me-1 mb-1">
                            {m.userId?.username || m.userId?.email || String(m.userId)}
                          </span>
                        ))}
                        {team.members.length > 3 && (
                          <span className="badge bg-light text-muted border">+{team.members.length - 3} more</span>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="card-footer bg-transparent border-0">
                    <button className="btn btn-outline-primary btn-sm w-100" onClick={() => openMembersModal(team)}>
                      Manage Members
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Create Team Modal */}
      {showCreateModal && (
        <div className="modal show d-block" style={{ background: 'rgba(0,0,0,.4)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Create Team</h5>
                <button className="btn-close" onClick={() => setShowCreateModal(false)} />
              </div>
              <div className="modal-body">
                {createError && <div className="alert alert-danger py-2">{createError}</div>}
                <div className="mb-3">
                  <label className="form-label fw-semibold">Team Name <span className="text-danger">*</span></label>
                  <input
                    type="text" className="form-control"
                    placeholder="e.g. QA Team Alpha"
                    value={createForm.name}
                    onChange={e => setCreateForm(p => ({ ...p, name: e.target.value }))}
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label fw-semibold">Description</label>
                  <textarea
                    className="form-control" rows={2}
                    placeholder="Optional description"
                    value={createForm.description}
                    onChange={e => setCreateForm(p => ({ ...p, description: e.target.value }))}
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={() => setShowCreateModal(false)}>Cancel</button>
                <button className="btn btn-primary" onClick={createTeam} disabled={createLoading}>
                  {createLoading ? 'Creating…' : 'Create Team'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Manage Members Modal */}
      {showMembersModal && (
        <div className="modal show d-block" style={{ background: 'rgba(0,0,0,.4)' }}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">👥 {showMembersModal.name} — Members</h5>
                <button className="btn-close" onClick={() => setShowMembersModal(null)} />
              </div>
              <div className="modal-body">
                {/* Add user */}
                <div className="d-flex gap-2 mb-4">
                  <select
                    className="form-select"
                    value={addUserForm}
                    onChange={e => setAddUserForm(e.target.value)}
                  >
                    <option value="">Select a user to add…</option>
                    {allUsers
                      .filter(u => !(showMembersModal.members || []).some(m =>
                        String(m.userId?._id || m.userId) === String(u._id || u.id)
                      ))
                      .map(u => (
                        <option key={u._id || u.id} value={u._id || u.id}>
                          {u.username} ({u.email})
                        </option>
                      ))}
                  </select>
                  <button className="btn btn-primary" onClick={addUserToTeam} disabled={addUserLoading}>
                    {addUserLoading ? '…' : 'Add'}
                  </button>
                </div>
                {addUserError && <div className="alert alert-danger py-2 mb-3">{addUserError}</div>}

                {/* Current members */}
                {(showMembersModal.members || []).length === 0 ? (
                  <p className="text-muted text-center py-3">No members yet.</p>
                ) : (
                  <table className="table table-hover align-middle">
                    <thead className="table-light">
                      <tr><th>User</th><th>Email</th><th>Added</th><th></th></tr>
                    </thead>
                    <tbody>
                      {(showMembersModal.members || []).map((m, i) => {
                        const uid = String(m.userId?._id || m.userId);
                        const uname = m.userId?.username || uid;
                        const email = m.userId?.email || '';
                        return (
                          <tr key={i}>
                            <td className="fw-semibold">{uname}</td>
                            <td className="text-muted">{email}</td>
                            <td className="text-muted small">{m.addedAt ? new Date(m.addedAt).toLocaleDateString() : '—'}</td>
                            <td>
                              <button className="btn btn-outline-danger btn-sm" onClick={() => removeUserFromTeam(uid)}>
                                Remove
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={() => setShowMembersModal(null)}>Close</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Teams;
